import { ChangeEventHandler, FocusEventHandler, ForwardedRef, forwardRef, FunctionComponent, ReactNode, useEffect, useMemo, useState } from "react";
import styles from "./TextField.module.css";
import Input from "../Input";
import FieldHeader from "../FieldHeader";
import FieldError from "../FieldError";

export interface ITextFieldProps {
  label?: string;
  multiline?: boolean;
  id?: string;
  type?: "text" | "number" | "password" | "date";
  name?: string;
  tooltip?: string;
  value?: string | number;
  disabled?: boolean;
  placeholder?: string;
  error?: boolean;
  maxLength?: number;
  min?: string | number;
  max?: string | number;
  disableArrows?: boolean;
  Icon?: FunctionComponent<{ className?: string }>;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  autofocus?: boolean;
  requiredIndicator?: ReactNode;
  step?: string;
  className?: string;
  messageError?: string;
}

const TextField = forwardRef(function TextField(
  props: ITextFieldProps,
  ref: ForwardedRef<HTMLTextAreaElement & HTMLInputElement>,
) {
  const Icon = props.Icon;

  const _className = useMemo(() => (
    [
      styles.root,
      props.disabled && styles.disabled,
      props.className,
    ].filter(x => x !== undefined).join(" ")
  ), [props.className, props.disabled, props.error, props.disableArrows]);

  return (
    <div className={styles.inputContainer}>
      <FieldHeader
        label={props.label}
        requiredIndicator={props.requiredIndicator}
        tooltip={props.tooltip}
        error={props.error}
        id={props.id}
      />
      <div className={styles.inputWithIconContainer}>
        {Icon && <Icon className={styles.icon} />}
        <Input
          ref={ref}
          multiline={props.multiline}
          id={props.id}
          type={props.type}
          name={props.name}
          value={props.value}
          disabled={props.disabled}
          placeholder={props.placeholder}
          error={props.error}
          onChange={props.onChange}
          onBlur={props.onBlur}
          maxLength={props.maxLength}
          disableArrows={props.disableArrows}
          min={props.min}
          max={props.max}
          autofocus={props.autofocus}
          step={props.step}
          className={_className}
        />
      </div>
      <FieldError error={props.error} message={props.messageError} />
    </div>
  );
});

export default TextField;
